import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, Lock, User, Eye, EyeOff, Loader2, CheckCircle2,
  ShieldCheck, Heart, ArrowRight, ArrowLeft, Star, Phone, Sparkles, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../App";
import { toast } from "sonner";
import RequestCallbackButton from "../components/RequestCallbackButton";
import SignupFlowExplainer from "../components/SignupFlowExplainer";
import { track } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_emergent-website-3/artifacts/6i1oj5oc_BTR_Health_favicon__2_-removebg-preview.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 1 = account, 2 = NDIS (optional)
  const nameRef = useRef(null);

  useEffect(() => {
    document.title = "Sign Up Free | BTR Health - NDIS Support Platform";
    track("signup_started", { user_type: "participant" });
    const t = setTimeout(() => {
      try { nameRef.current && nameRef.current.focus({ preventScroll: true }); } catch (_) {}
    }, 250);
    return () => clearTimeout(t);
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    ndis_plan_type: "",
    ndis_number: "",
    dob: "",
    address: "",
    plan_manager_name: "",
    plan_manager_email: "",
    agree_terms: false,
  });
  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Your name is required";
    if (!form.email) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.phone.trim()) e.phone = "Mobile is required so workers can reach you";
    if (!form.password) e.password = "Password is required";
    else if (form.password.length < 6) e.password = "Minimum 6 characters";
    if (!form.confirmPassword) e.confirmPassword = "Please confirm your password";
    else if (form.confirmPassword !== form.password) e.confirmPassword = "Passwords don't match";
    if (!form.agree_terms) e.agree_terms = "Please accept the Terms and Privacy Policy to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goToStep2 = (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    if (!validateStep1()) return;
    track("signup_step1_done", { user_type: "participant" });
    setStep(2);
  };

  const submitFinal = async (ev) => {
    if (ev?.preventDefault) ev.preventDefault();
    track("signup_step2_done", { user_type: "participant", skipped_ndis: !form.ndis_plan_type });
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        user_type: "participant",
        phone: form.phone.trim() || undefined,
        ndis_plan_type: form.ndis_plan_type || undefined,
        ndis_number: form.ndis_number.trim() || undefined,
        dob: form.dob || undefined,
        address: form.address.trim() || undefined,
        plan_manager_name: form.plan_manager_name.trim() || undefined,
        plan_manager_email: form.plan_manager_email.trim() || undefined,
      };
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (response.status === 400) {
          let detail = "";
          try { detail = String((await response.json())?.detail || ""); } catch (_) {}
          if (!detail || /already registered/i.test(detail)) {
            throw new Error("This email is already registered. Please sign in instead.");
          }
          throw new Error(detail);
        }
        let detail = "Sign up failed. Please try again.";
        try { detail = String((await response.json())?.detail || detail); } catch (_) {}
        throw new Error(detail);
      }

      const data = await response.json();
      track("signup_completed", { user_type: "participant", method: "email" });
      try { if (data.token) localStorage.setItem("auth_token", String(data.token)); }
      catch (err) { console.warn("[register] could not persist token", err); }

      try {
        login({
          user_id: String(data.user_id || ""),
          email: String(data.email || ""),
          name: String(data.name || ""),
          user_type: "participant",
          picture: null,
        }, data.token ? String(data.token) : null);
      } catch (err) { console.warn("[register] login() threw", err); }

      // Multiple navigation strategies — some browsers/extensions block one but allow others.
      // We intentionally swallow per-strategy errors and fall through to the next.
      try { window.location.assign("/onboarding"); return; } catch (_) { /* try next */ }
      try { window.location.href = "/onboarding"; return; } catch (_) { /* try next */ }
      try { navigate("/onboarding"); } catch (err) { console.warn("[register] all nav strategies failed", err); }
    } catch (error) {
      const raw = error?.message || String(error);
      const isClone = /clone|DataClone|cloned/i.test(raw);
      const msg = isClone
        ? "Almost done — please refresh the page to finish signing in."
        : raw || "Sign up failed. Please try again.";
      try { toast.error(msg); } catch (_) {}
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    track("signup_started", { user_type: "participant", method: "google" });
    track("signup_step1_done", { user_type: "participant", method: "google" });
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = window.location.origin + "/auth/callback";
    const scope = "openid email profile";
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent&state=participant`;
    window.location.href = authUrl;
  };

  // ===== UI =====
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-white flex flex-col">
      {/* Top bar */}
      <header className="px-4 sm:px-6 py-3.5 flex items-center justify-between border-b border-slate-100 bg-white/80 backdrop-blur sticky top-0 z-30">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="register-home-link">
          <img src={LOGO_ICON} alt="BTR Health" className="h-7 w-7" />
          <span className="font-bold text-base text-slate-900">BTR Health</span>
        </Link>
        <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900" data-testid="register-signin-link">
          Already a member? <span className="text-[#87CEEB] font-semibold">Sign in</span>
        </Link>
      </header>

      <main className="flex-1 grid lg:grid-cols-2 max-w-6xl w-full mx-auto">
        {/* LEFT — explainer + value */}
        <aside className="hidden lg:flex flex-col justify-center p-8 xl:p-12 bg-gradient-to-br from-[#87CEEB]/15 via-white to-[#87CEEB]/5 border-r border-slate-100 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#87CEEB]/40 text-[#5BA3C0] text-xs font-semibold mb-4 shadow-sm">
              <Sparkles className="w-3.5 h-3.5" />
              Free forever for NDIS participants
            </div>

            <SignupFlowExplainer variant="participant" />

            {/* Social proof footer */}
            <div className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl border border-slate-100 shadow-sm mt-5">
              <div className="flex -space-x-2">
                {[0,1,2,3].map((i) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-white shadow-sm"
                    style={{ background: ["#87CEEB","#FBBF24","#34D399","#F472B6"][i] }}
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-amber-500">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-amber-400" />)}
                  <span className="ml-1 text-[11px] font-semibold text-slate-900">5.0</span>
                </div>
                <p className="text-[11px] text-slate-500 leading-tight">Loved by NDIS participants & their families</p>
              </div>
            </div>
          </motion.div>
        </aside>

        {/* RIGHT — form */}
        <section className="flex items-center justify-center p-5 sm:p-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="w-full max-w-md"
          >
            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-5" data-testid="register-step-indicator">
              <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 1 ? "bg-[#87CEEB]" : "bg-slate-200"}`} />
              <div className={`flex-1 h-1 rounded-full transition-colors ${step >= 2 ? "bg-[#87CEEB]" : "bg-slate-200"}`} />
              <span className="text-[11px] font-semibold text-slate-400 tabular-nums">Step {step} of 2</span>
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="text-center lg:text-left mb-6">
                    <h1 className="text-2xl sm:text-[28px] font-bold text-slate-900 tracking-tight">Create your free account</h1>
                    <p className="text-slate-500 text-sm sm:text-[15px] mt-1.5">Takes under a minute — Google or email.</p>
                  </div>

                  {/* Google — primary 1-tap path */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 rounded-xl border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all font-semibold text-slate-800"
                    onClick={handleGoogleSignup}
                    data-testid="google-signup-btn"
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </Button>
                  <p className="text-center text-[11px] text-slate-400 mt-1.5">⚡ Fastest — one tap, no password to remember</p>

                  <div className="relative my-5">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                    <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-slate-400">or sign up with email</span></div>
                  </div>

                  <form onSubmit={goToStep2} className="space-y-3" data-testid="register-form">
                    {/* Name */}
                    <div>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <Input
                          ref={nameRef}
                          type="text"
                          placeholder="Your full name"
                          autoComplete="name"
                          enterKeyHint="next"
                          className={`pl-11 h-12 rounded-xl text-[15px] ${errors.name ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          aria-label="Full name"
                          data-testid="register-name-input"
                        />
                      </div>
                      {errors.name && <p className="text-red-500 text-xs mt-1 ml-1">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <Input
                          type="email"
                          placeholder="Email address"
                          autoComplete="email"
                          inputMode="email"
                          enterKeyHint="next"
                          className={`pl-11 h-12 rounded-xl text-[15px] ${errors.email ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          aria-label="Email address"
                          data-testid="register-email-input"
                        />
                      </div>
                      {errors.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <Input
                          type="tel"
                          placeholder="Mobile number"
                          autoComplete="tel"
                          inputMode="tel"
                          enterKeyHint="next"
                          className={`pl-11 h-12 rounded-xl text-[15px] ${errors.phone ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          aria-label="Mobile number"
                          data-testid="register-phone-input"
                        />
                      </div>
                      {errors.phone && <p className="text-red-500 text-xs mt-1 ml-1">{errors.phone}</p>}
                    </div>

                    {/* Password + Confirm — side by side for compactness */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Password (6+)"
                            autoComplete="new-password"
                            enterKeyHint="next"
                            className={`pl-11 pr-10 h-12 rounded-xl text-[15px] ${errors.password ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            aria-label="Password"
                            data-testid="register-password-input"
                          />
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            onClick={() => setShowPassword((s) => !s)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {errors.password && <p className="text-red-500 text-xs mt-1 ml-1">{errors.password}</p>}
                      </div>
                      <div>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Confirm"
                            autoComplete="new-password"
                            enterKeyHint="done"
                            className={`pl-11 h-12 rounded-xl text-[15px] ${errors.confirmPassword ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                            value={form.confirmPassword}
                            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                            aria-label="Confirm password"
                            data-testid="register-confirm-password-input"
                          />
                        </div>
                        {errors.confirmPassword && <p className="text-red-500 text-xs mt-1 ml-1">{errors.confirmPassword}</p>}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 rounded-xl bg-[#87CEEB] hover:bg-[#6BB8DB] active:scale-[0.99] text-white font-bold text-[15px] mt-2 shadow-md shadow-[#87CEEB]/30 transition-all"
                      data-testid="register-next-btn"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>

                    {/* Explicit Terms / Privacy consent — required by Australian Privacy Principles & NDIS compliance. */}
                    <label className="flex items-start gap-2.5 mt-3 cursor-pointer select-none" data-testid="register-terms-consent">
                      <input
                        type="checkbox"
                        className="mt-1 w-4 h-4 rounded border-slate-300 text-[#87CEEB] focus:ring-[#87CEEB]"
                        checked={form.agree_terms}
                        onChange={(e) => setForm({ ...form, agree_terms: e.target.checked })}
                        aria-label="Accept terms and privacy policy"
                        data-testid="register-terms-checkbox"
                      />
                      <span className="text-[12px] text-slate-600 leading-snug">
                        I agree to BTR Health's{" "}
                        <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#5BA3C0] hover:underline font-semibold">Terms of Service</Link>{" "}
                        and{" "}
                        <Link to="/privacy" target="_blank" rel="noreferrer" className="text-[#5BA3C0] hover:underline font-semibold">Privacy Policy</Link>, and consent to BTR Health collecting and handling my personal and NDIS information to deliver supports in line with the{" "}
                        <span className="font-semibold">NDIS Code of Conduct</span>.
                      </span>
                    </label>
                    {errors.agree_terms && <p className="text-red-500 text-xs mt-1 ml-1" data-testid="register-terms-error">{errors.agree_terms}</p>}

                    <p className="text-center text-[11px] text-slate-400">
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Free forever</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> No credit card</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> 1-click cancel</span>
                    </p>
                  </form>

                  {/* Mobile-only mini trust strip */}
                  <div className="lg:hidden mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#87CEEB]" /> Police-checked workers</span>
                    <span className="text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3 text-rose-500" /> $0 fees</span>
                    <span className="text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> 5.0 rated</span>
                  </div>

                  {/* Mobile-only flow explainer */}
                  <SignupFlowExplainer variant="participant" compact />

                  {/* Callback alternative */}
                  <div className="mt-5 flex justify-center">
                    <RequestCallbackButton context="register_page" />
                  </div>

                  <p className="text-center text-xs text-slate-500 mt-4">
                    Want to work as a support worker?{" "}
                    <Link to="/worker-signup" className="text-[#87CEEB] hover:underline font-semibold" data-testid="register-worker-link">
                      Join as a worker
                    </Link>
                  </p>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="text-center lg:text-left mb-6">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#87CEEB]/10 text-[#5BA3C0] text-[11px] font-semibold mb-3">
                      <FileText className="w-3 h-3" />
                      Optional · skip if paying privately
                    </div>
                    <h1 className="text-2xl sm:text-[28px] font-bold text-slate-900 tracking-tight">Funding details</h1>
                    <p className="text-slate-500 text-sm sm:text-[15px] mt-1.5">
                      Not on the NDIS? Just hit <span className="font-semibold text-slate-700">Skip</span> below — many of our participants pay privately. Adding these details lets workers quote correctly from day one.
                    </p>
                  </div>

                  <form onSubmit={submitFinal} className="space-y-4">
                    <div>
                      <label htmlFor="ndis-plan-type" className="text-[13px] font-semibold text-slate-700">How is your support funded?</label>
                      <select
                        id="ndis-plan-type"
                        value={form.ndis_plan_type}
                        onChange={(e) => setForm({ ...form, ndis_plan_type: e.target.value })}
                        className="mt-1.5 w-full h-12 rounded-xl border border-slate-200 bg-white px-3 text-[14px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#87CEEB]"
                        data-testid="register-ndis-plan-type"
                      >
                        <option value="">Choose one (optional)</option>
                        <option value="self_managed">NDIS · Self-managed</option>
                        <option value="plan_managed">NDIS · Plan-managed</option>
                        <option value="agency_managed">NDIS · NDIA / Agency-managed</option>
                        <option value="private">Paying privately (not on NDIS)</option>
                        <option value="not_sure">I'm not sure yet</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="ndis-number" className="text-[13px] font-semibold text-slate-700">
                        NDIS participant number <span className="text-slate-400 font-normal">— skip if not applicable</span>
                      </label>
                      <Input
                        id="ndis-number"
                        type="text"
                        placeholder="9-digit NDIS number (optional)"
                        inputMode="numeric"
                        className="mt-1.5 h-12 rounded-xl text-[15px]"
                        value={form.ndis_number}
                        onChange={(e) => setForm({ ...form, ndis_number: e.target.value })}
                        aria-label="NDIS participant number"
                        data-testid="register-ndis-number-input"
                      />
                      <p className="text-[11px] text-slate-400 mt-1">
                        Used only for matching · never shared publicly · leave blank if you're paying privately.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="dob" className="text-[13px] font-semibold text-slate-700">
                        Date of birth <span className="text-slate-400 font-normal">— used on your NDIS Service Agreement</span>
                      </label>
                      <Input
                        id="dob"
                        type="date"
                        className="mt-1.5 h-12 rounded-xl text-[15px]"
                        value={form.dob}
                        onChange={(e) => setForm({ ...form, dob: e.target.value })}
                        max={new Date().toISOString().slice(0, 10)}
                        aria-label="Date of birth"
                        data-testid="register-dob-input"
                      />
                      <p className="text-[11px] text-slate-400 mt-1">
                        Required by NDIS providers to uniquely identify you on the signed Service Agreement.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="address" className="text-[13px] font-semibold text-slate-700">
                        Residential address <span className="text-slate-400 font-normal">— street, suburb, state, postcode</span>
                      </label>
                      <Input
                        id="address"
                        type="text"
                        autoComplete="street-address"
                        placeholder="e.g. 12 Smith St, Bondi NSW 2026"
                        className="mt-1.5 h-12 rounded-xl text-[15px]"
                        value={form.address}
                        onChange={(e) => setForm({ ...form, address: e.target.value })}
                        aria-label="Residential address"
                        data-testid="register-address-input"
                      />
                      <p className="text-[11px] text-slate-400 mt-1">
                        Embedded into your Service Agreement. We never share it publicly.
                      </p>
                    </div>

                    {form.ndis_plan_type === "plan_managed" && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-xl border border-[#87CEEB]/30 bg-[#87CEEB]/5 p-3 space-y-3"
                        data-testid="register-plan-manager-block"
                      >
                        <p className="text-[12px] font-semibold text-[#4FA3C7]">Plan manager details</p>
                        <div>
                          <label htmlFor="pm-name" className="text-[12px] font-semibold text-slate-700">Plan manager name</label>
                          <Input
                            id="pm-name"
                            type="text"
                            placeholder="e.g. My Plan Manager Pty Ltd"
                            className="mt-1 h-11 rounded-xl text-[14px]"
                            value={form.plan_manager_name}
                            onChange={(e) => setForm({ ...form, plan_manager_name: e.target.value })}
                            data-testid="register-plan-manager-name"
                          />
                        </div>
                        <div>
                          <label htmlFor="pm-email" className="text-[12px] font-semibold text-slate-700">Plan manager email (for invoicing)</label>
                          <Input
                            id="pm-email"
                            type="email"
                            placeholder="invoices@yourplanmanager.com.au"
                            className="mt-1 h-11 rounded-xl text-[14px]"
                            value={form.plan_manager_email}
                            onChange={(e) => setForm({ ...form, plan_manager_email: e.target.value })}
                            data-testid="register-plan-manager-email"
                          />
                        </div>
                        <p className="text-[11px] text-slate-500">
                          We invoice your plan manager directly so you never see a bill. Add it later if you don't have it on hand.
                        </p>
                      </motion.div>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-12 rounded-xl sm:w-auto px-5 font-semibold text-slate-700"
                        onClick={() => setStep(1)}
                        data-testid="register-back-btn"
                      >
                        <ArrowLeft className="w-4 h-4 mr-1.5" />
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="flex-1 h-12 rounded-xl bg-[#87CEEB] hover:bg-[#6BB8DB] active:scale-[0.99] text-white font-bold text-[15px] shadow-md shadow-[#87CEEB]/30 transition-all"
                        data-testid="register-submit-btn"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            Create my account
                            <ArrowRight className="w-4 h-4 ml-1.5" />
                          </>
                        )}
                      </Button>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); submitFinal(); }}
                      disabled={loading}
                      className="block w-full text-center text-[12.5px] text-slate-500 hover:text-slate-700 underline pt-1 disabled:opacity-50"
                      data-testid="register-skip-ndis-btn"
                    >
                      Skip — I'm paying privately, or I'll add it later
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </section>
      </main>
    </div>
  );
}
